// 온셋 검출 — 렌더된 파형에서 「소리가 시작된 자리」를 찾는다
//
// **왜 시작하는 자리인가.** 옛 판정 방법은 진폭이 0으로 떨어지는 샘플 인덱스를 찾아
// 끊김 지점을 개시로 삼았다. 그 방법은 소리가 한 덩어리일 때만 성립한다. 지금 형태는
// 소리가 멎는 자리가 여섯 번이라 어느 것이 개시인지 가려낼 근거가 없다.
//
// **왜 진폭 절반인가.** 온셋 램프가 목표 시각에 걸쳐 있으므로(게인 0이 목표보다 앞,
// 최대가 뒤) 진폭이 그 이벤트 최대의 절반에 닿는 순간이 정확히 목표 시각이다. 소리가
// 어떤 고정 임계를 처음 넘는 지점을 온셋으로 삼으면 램프 길이만큼 통째로 이르게
// 검출되고, 그 편향은 판정이 요구하는 ±1밀리초보다 크다.
//
// **왜 RMS 포락선인가.** 사인의 순간 진폭은 한 주기 안에서 두 번 0을 지난다. 절댓값을
// 그대로 쓰면 소리가 나는 동안에도 포락선이 매 주기 바닥까지 내려가 한 이벤트가 수십
// 개로 쪼개진다. 가장 낮은 음의 한 주기보다 긴 창으로 RMS 를 내면 그 진동이 평활된다.
// 창을 목표 시각 **양쪽으로 대칭**으로 두는 것이 요점이다 — 한쪽으로 치우친 창은 그
// 치우친 만큼 온셋을 통째로 옮긴다.
//
// 이 파일은 프레임워크도 브라우저 API 도 쓰지 않는다. 그래서 Node 안의 단위 테스트가
// 손으로 그린 정답 파형으로 이 검출기를 잴 수 있다.

export interface DetectedEvent {
  /** 진폭이 절반에 닿는 상승 지점. 사람이 소리의 시작으로 듣는 자리다. */
  readonly onsetSec: number
  /** 진폭이 절반으로 떨어지는 하강 지점. */
  readonly endSec: number
  readonly durationSec: number
  /** 이 이벤트의 최대 RMS. */
  readonly peak: number
  /** 영교차로 잰 지배 주파수. */
  readonly frequencyHz: number
  /** 최대 진폭을 실효값으로 나눈 값. 순수한 사인이면 √2 에 가깝다. */
  readonly crestFactor: number
}

export interface DetectOptions {
  readonly sampleRate: number
  readonly envelopeWindowSec?: number
  readonly floorRatio?: number
}

/** 880 Hz 한 주기(약 1.14밀리초)보다 길고, 램프를 뭉개지 않을 만큼 짧다. */
export const DEFAULT_ENVELOPE_WINDOW_SEC = 0.002

/** 전역 최대 대비 이 비율 아래는 침묵으로 본다. */
export const DEFAULT_FLOOR_RATIO = 0.02

/** 전역 최대 대비 이만큼도 안 되는 덩어리는 이벤트로 세지 않는다 — 꼬리와 잔물결이다. */
const MIN_EVENT_PEAK_RATIO = 0.08

/**
 * 대칭 창 RMS 포락선. 누적 제곱합을 한 번 만들어 두고 창마다 빼서 O(n) 으로 낸다 —
 * 창마다 다시 더하면 5초 버퍼에서 수억 번의 곱셈이 되고, 브라우저에서 화면이 멈춘다.
 */
export function rmsEnvelope(
  samples: Float32Array,
  sampleRate: number,
  windowSec: number = DEFAULT_ENVELOPE_WINDOW_SEC,
): Float32Array {
  const n = samples.length
  const out = new Float32Array(n)
  if (n === 0) return out

  const width = Math.max(1, Math.round(windowSec * sampleRate))
  const half = Math.floor(width / 2)

  // 누적합은 배정밀도로 쌓는다. 단정밀도로 쌓으면 긴 버퍼의 뒤쪽에서 작은 항이
  // 통째로 삼켜져 포락선이 조용히 낮아진다.
  const prefix = new Float64Array(n + 1)
  for (let i = 0; i < n; i += 1) {
    const v = samples[i] ?? 0
    prefix[i + 1] = (prefix[i] ?? 0) + v * v
  }

  for (let i = 0; i < n; i += 1) {
    const lo = Math.max(0, i - half)
    const hi = Math.min(n, i + half + 1)
    const sum = (prefix[hi] ?? 0) - (prefix[lo] ?? 0)
    out[i] = Math.sqrt(sum / (hi - lo))
  }
  return out
}

/** 두 샘플 사이에서 임계를 지나는 지점을 직선으로 보간한다. 샘플 격자보다 곱게 잰다. */
function crossingIndex(before: number, after: number, threshold: number, index: number): number {
  const span = after - before
  if (span === 0) return index
  return index - 1 + (threshold - before) / span
}

function estimateFrequency(
  samples: Float32Array,
  fromIndex: number,
  toIndex: number,
  sampleRate: number,
): number {
  let crossings = 0
  let previous = 0
  for (let i = fromIndex; i <= toIndex; i += 1) {
    const v = samples[i] ?? 0
    if (v === 0) continue
    const sign = v > 0 ? 1 : -1
    if (previous !== 0 && sign !== previous) crossings += 1
    previous = sign
  }
  const spanSec = (toIndex - fromIndex) / sampleRate
  if (spanSec <= 0) return 0
  return crossings / (2 * spanSec)
}

function estimateCrestFactor(samples: Float32Array, fromIndex: number, toIndex: number): number {
  // 램프를 빼고 가운데 절반만 본다. 램프가 섞이면 실효값이 내려가 어떤 파형이든
  // 첨두율이 높아 보이고, 그러면 사각파도 사인으로 읽힌다.
  const quarter = Math.floor((toIndex - fromIndex) / 4)
  const from = fromIndex + quarter
  const to = toIndex - quarter
  if (to <= from) return 0

  let peak = 0
  let energy = 0
  for (let i = from; i <= to; i += 1) {
    const v = Math.abs(samples[i] ?? 0)
    if (v > peak) peak = v
    energy += v * v
  }
  const rms = Math.sqrt(energy / (to - from + 1))
  if (rms === 0) return 0
  return peak / rms
}

/**
 * 소리가 나는 덩어리를 찾아 각각의 온셋·길이·음고를 잰다.
 *
 * 덩어리를 먼저 가르고 **덩어리마다 자기 최대의 절반**을 임계로 삼는다. 전역 임계
 * 하나를 쓰면 조용한 표지와 큰 종점이 다른 기준으로 재어져, 둘의 시각 차이에 진폭
 * 차이가 섞여 들어간다.
 */
export function detectEvents(samples: Float32Array, options: DetectOptions): DetectedEvent[] {
  const { sampleRate } = options
  const envelope = rmsEnvelope(
    samples,
    sampleRate,
    options.envelopeWindowSec ?? DEFAULT_ENVELOPE_WINDOW_SEC,
  )

  let globalPeak = 0
  for (const v of envelope) if (v > globalPeak) globalPeak = v
  if (globalPeak <= 0) return []

  const floor = globalPeak * (options.floorRatio ?? DEFAULT_FLOOR_RATIO)

  // 걸음 하나 — 바닥 위로 이어지는 덩어리를 모은다.
  const runs: { from: number; to: number }[] = []
  let openedAt = -1
  for (let i = 0; i <= envelope.length; i += 1) {
    const loud = i < envelope.length && (envelope[i] ?? 0) > floor
    if (loud) {
      if (openedAt < 0) openedAt = i
    } else if (openedAt >= 0) {
      runs.push({ from: openedAt, to: i - 1 })
      openedAt = -1
    }
  }

  // 걸음 둘 — 덩어리마다 자기 최대의 절반을 임계로 삼아 잰다. 전역 임계 하나를 쓰면
  // 조용한 표지와 큰 종점이 다른 기준으로 재어져, 둘의 시각 차이에 진폭 차이가 섞인다.
  const events: DetectedEvent[] = []
  for (const run of runs) {
    let peak = 0
    for (let j = run.from; j <= run.to; j += 1) {
      const v = envelope[j] ?? 0
      if (v > peak) peak = v
    }
    if (peak < globalPeak * MIN_EVENT_PEAK_RATIO) continue

    const halfPeak = peak / 2
    let riseIndex = run.from
    while (riseIndex <= run.to && (envelope[riseIndex] ?? 0) < halfPeak) riseIndex += 1
    let fallIndex = run.to
    while (fallIndex >= run.from && (envelope[fallIndex] ?? 0) < halfPeak) fallIndex -= 1

    const onsetIdx = crossingIndex(
      envelope[Math.max(run.from, riseIndex - 1)] ?? 0,
      envelope[riseIndex] ?? 0,
      halfPeak,
      riseIndex,
    )
    const endIdx = crossingIndex(
      envelope[Math.min(run.to, fallIndex + 1)] ?? 0,
      envelope[fallIndex] ?? 0,
      halfPeak,
      fallIndex + 1,
    )
    const onsetSec = onsetIdx / sampleRate
    const endSec = Math.max(endIdx, onsetIdx) / sampleRate

    events.push({
      onsetSec,
      endSec,
      durationSec: endSec - onsetSec,
      peak,
      frequencyHz: estimateFrequency(samples, riseIndex, fallIndex, sampleRate),
      crestFactor: estimateCrestFactor(samples, riseIndex, fallIndex),
    })
  }

  return events
}

/**
 * 구간 안에서 소리가 나는 샘플의 비율. 「오래 들어도 괴롭지 않을 것」의 기계 몫이다.
 *
 * 여기서는 이벤트 최대가 아니라 **전역 바닥**을 임계로 쓴다. 재려는 것이 「어느
 * 이벤트가 언제 시작했는가」가 아니라 「구간이 소리로 얼마나 차 있는가」라, 램프까지
 * 포함해 소리로 세는 편이 사람이 듣는 것에 가깝다.
 */
export function soundingRatio(
  samples: Float32Array,
  options: DetectOptions & { fromSec: number; toSec: number },
): number {
  const { sampleRate, fromSec, toSec } = options
  const envelope = rmsEnvelope(
    samples,
    sampleRate,
    options.envelopeWindowSec ?? DEFAULT_ENVELOPE_WINDOW_SEC,
  )

  let globalPeak = 0
  for (const v of envelope) if (v > globalPeak) globalPeak = v
  if (globalPeak <= 0) return 0

  const floor = globalPeak * (options.floorRatio ?? DEFAULT_FLOOR_RATIO)
  const from = Math.max(0, Math.round(fromSec * sampleRate))
  const to = Math.min(envelope.length, Math.round(toSec * sampleRate))
  if (to <= from) return 0

  let sounding = 0
  for (let i = from; i < to; i += 1) if ((envelope[i] ?? 0) > floor) sounding += 1
  return sounding / (to - from)
}
